import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getResearchersLeaderboard
} from "../../utils/api/endpoints/researcher/profile";

	type Row = {
	  rank: number;
	  user_id: number;
	  name: string;
	  pfp?: string | null;
	  joinedAt?: string | null;
	  country?: string | null;
	  approved: boolean;
	  // Adjusted reputation after SLA penalties (primary points value)
	  reputation: number;
	  // Optional extra fields from API for clarity
	  baseReputation?: number;
	  adjustedReputation?: number;
	  slaPenaltyPoints?: number;
	  totalReports: number;
	  verifiedReports: number;
	  pendingReports: number;
	  criticalFinds: number;
	  specialization?: string | null;
	  status?: string | null;
	};

const Leaderboard = ({
  viewProfilePath
}: {
  viewProfilePath?: (userId: number) => string;
} = {}) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [q, setQ] = useState("");
  const [approved, setApproved] = useState<string>("true");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const formatSpec = (raw?: string | null) => {
    if (!raw) return "";
    const r = String(raw).trim();
    const lower = r.toLowerCase();
    if (lower === "api" || r === "API" || r === "API Security") return r.includes("Security") ? "API Security" : "API";
    if (lower === "ios") return r.includes("Security") ? "iOS Security" : "iOS";
    return r
      .toLowerCase()
      .split(/\s+/)
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  };

  const approvedParam = useMemo(() => {
    if (approved === "true") return true;
    if (approved === "false") return false;
    return undefined;
  }, [approved]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const data = await getResearchersLeaderboard({
          page,
          pageSize,
          q: q || undefined,
          approved: approvedParam as any,
          status: status || undefined
        });
        if (!mounted) return;
        setRows(data.items || []);
        setTotal(data.total || 0);
      } catch {}
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, [page, pageSize, q, approvedParam, status]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(total / pageSize));
  }, [total, pageSize]);

  const paginationItems = useMemo(() => {
    const current = page;
    const last = totalPages;
    const delta = 2; // show two numbers on each side
    const range: Array<number> = [];
    const rangeWithDots: Array<number | string> = [];
    let prev: number | undefined;

    for (let i = 1; i <= last; i++) {
      if (i === 1 || i === last || (i >= current - delta && i <= current + delta)) {
        range.push(i);
      }
    }

    for (const i of range) {
      if (prev !== undefined) {
        if (i - prev === 2) {
          rangeWithDots.push(prev + 1);
        } else if (i - prev > 2) {
          rangeWithDots.push("…");
        }
      }
      rangeWithDots.push(i);
      prev = i;
    }
    return rangeWithDots;
  }, [page, totalPages]);

  return (
    <div className="p-6">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-xl font-semibold text-[#0B1739]">Leaderboard</h2>
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full">
          <img src="/icons/box7.svg" alt="leaderboard" className="h-4 w-4" />
        </span>
      </div>
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex w-full gap-3 md:w-auto">
          <input
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-500 md:w-80"
            placeholder="Search researcher by Username"
            value={q}
            onChange={e => setQ(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-3 md:ml-auto">
          <span className="text-sm text-gray-500">Total: {total}</span>
          <select
            className="rounded-lg border border-gray-200 bg-white px-3 py-2"
            value={approved}
            onChange={e => setApproved(e.target.value)}
          >
            <option value="all">All Researchers</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-100">
        <table className="min-w-full divide-y divide-gray-100">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Rank</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Researcher</th>
	              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Points</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Vulnerabilities</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Critical Finds</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Specialization</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-sm text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-sm text-gray-500">
                  No results
                </td>
              </tr>
            ) : (
              rows.map(r => (
                <tr key={r.user_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-700">{r.rank}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <img
                        src={r.pfp || "/icons/box1.svg"}
                        alt="avatar"
                        className="h-10 w-10 rounded-full object-cover"
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{r.name}</div>
                        <div className="text-xs text-gray-500">{
                          r.joinedAt
                            ? `Joined ${new Date(r.joinedAt).toLocaleString("en-US", {
                                month: "short",
                                year: "numeric"
                              })}`
                            : "—"
                        }</div>
                      </div>
                    </div>
                  </td>
	                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">
	                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
	                      <span>
	                        {typeof (r.adjustedReputation ?? r.reputation) === "number"
	                          ? (r.adjustedReputation ?? r.reputation)
	                          : "-"}
	                      </span>
	                      {r.slaPenaltyPoints && r.slaPenaltyPoints > 0 && (
	                        <span className="flex items-center text-xs text-red-500">
	                          <span className="mr-0.5">▼</span>
	                          <span>-{r.slaPenaltyPoints}</span>
	                        </span>
	                      )}
	                    </div>
	                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {r.totalReports}
                    <div className="text-xs text-gray-500">{r.verifiedReports} verified, {r.pendingReports} pending</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">{r.criticalFinds}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {r.specialization ? (
                      <span className="inline-flex rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                        {formatSpec(r.specialization)}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${r.status?.toLowerCase() === "active" ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-600"}`}>{r.status || "—"}</span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <button
                      className="inline-flex items-center gap-1"
                      style={{ color: "#58CC02" }}
                      onClick={() => {
                        const path = viewProfilePath
                          ? viewProfilePath(r.user_id)
                          : `/dashboard/profile/${r.user_id}`;
                        navigate(path);
                        // After navigation, force the scrollable container to top
                        setTimeout(() => {
                          const el = document.getElementById('dashboard-main-scroll');
                          if (el) el.scrollTop = 0;
                          try { window.scrollTo({ top: 0, behavior: 'auto' }); } catch {}
                          const sc = (document.scrollingElement || document.documentElement);
                          if (sc) sc.scrollTop = 0;
                          document.body.scrollTop = 0;
                        }, 0);
                      }}
                    >
                      <span>View</span>
                      <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 111.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-6 flex items-center justify-center">
        <div className="inline-flex items-center overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <button
            className="px-5 py-2 text-sm text-gray-700 disabled:text-gray-300"
            disabled={page <= 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
          >
            Previous
          </button>
          {paginationItems.map((it, idx) => (
            typeof it === 'string' ? (
              <span key={idx} className="px-4 py-2 text-sm text-gray-500">{it}</span>
            ) : (
              <button
                key={idx}
                className={`px-4 py-2 text-sm ${it === page ? 'font-semibold text-white' : 'text-gray-700'} `}
                style={it === page ? { backgroundColor: '#0B144B' } : {}}
                onClick={() => setPage(it)}
              >
                {it}
              </button>
            )
          ))}
          <button
            className="px-5 py-2 text-sm text-gray-700 disabled:text-gray-300"
            disabled={page >= totalPages}
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;


